/**
 * Cartão "Evolução auditiva" da ficha do trabalhador (#461, telas 3, 4 e 5 do mockup). Tudo o que
 * compara vem pronto do servidor; a tela só escreve. Sem `medical-records.read` o cartão não
 * existe — nunca se diz "nenhuma audiometria" a quem só enxerga uma fatia.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { Hearing } from '@mui/icons-material';

import { pode } from '../../../authorization/pode';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import { useControle } from '../../../authorization/useVeredito';
import { ActionButton } from '../../../components/common/ActionButton';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/common/DataTable';
import { EmptyState } from '../../../components/common/EmptyState';
import { audiometryService } from '../../../services/audiometryService';
import type { AudiometryHistory, AudiometryHistoryExam } from '../../../types/audiometry';
import { AudiogramaDaOrelha } from './AudiogramaDaOrelha';
import {
  ROTULO_DO_PAPEL,
  formatarMedias,
  primeiroNome,
  situacaoDoExame,
  temLimiares,
} from './evolucaoAuditiva';
import { descreverPiora, formatarDia, mesmoId } from '../../../utils/audiometria';

interface EmployeeAudiometryCardProps {
  employeeId: number;
  employeeName?: string;
}

const ACCENT = '#1565C0';
const PERMISSAO_DE_LEITURA = 'medical-records.read';
const PERMISSAO_DE_MARCAR = 'appointment.update';

function mensagemDeErro(erro: unknown, padrao: string): string {
  const resposta = (erro as { response?: { data?: { message?: string; error?: string } } } | null)?.response;
  return resposta?.data?.message ?? resposta?.data?.error ?? (erro instanceof Error ? erro.message : padrao);
}

function contarAudiometrias(total: number): string {
  return total === 1 ? '1 audiometria registrada' : `${total} audiometrias registradas`;
}

const Legenda: React.FC<{ texto: string; tracejada: boolean }> = ({ texto, tracejada }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box
      aria-hidden="true"
      sx={{ width: 28, borderTop: `3px ${tracejada ? 'dashed' : 'solid'}`, borderColor: 'text.primary' }}
    />
    <Typography variant="body2">{texto}</Typography>
  </Stack>
);

export const EmployeeAudiometryCard: React.FC<EmployeeAudiometryCardProps> = ({ employeeId, employeeName }) => {
  const navigate = useNavigate();
  const abrirAtendimentos = useControle('/medical-consultations', pode('medical-records.read'), 'Abrir atendimentos');
  const [candidata, setCandidata] = useState<AudiometryHistoryExam | null>(null);
  const [gravando, setGravando] = useState(false);
  const [erroAoMarcar, setErroAoMarcar] = useState<string | null>(null);

  const carregar = useCallback(() => audiometryService.getHistory(employeeId), [employeeId]);
  const historico = useDadoPermitido<AudiometryHistory>(PERMISSAO_DE_LEITURA, 'Evolução auditiva', carregar);
  const { dado: dados, recarregar } = historico;

  const exames = useMemo(() => dados?.exams ?? [], [dados]);
  const ultimo = exames.find(temLimiares) ?? null;
  const referencia = dados?.effectiveReference ? (exames.find((e) => e.isReference) ?? null) : null;
  const ultimoEhAReferencia = ultimo !== null && referencia !== null && mesmoId(ultimo.appointmentExamId, referencia.appointmentExamId);
  const primeiro = primeiroNome(employeeName);
  const deQuem = primeiro ? `de ${primeiro}` : 'do trabalhador';

  const marcar = useCallback(
    async (exame: AudiometryHistoryExam) => {
      setGravando(true);
      setErroAoMarcar(null);
      try {
        await audiometryService.setAsReference(Number(exame.appointmentId), Number(exame.appointmentExamId));
        setCandidata(null);
        recarregar();
      } catch (erro: unknown) {
        setErroAoMarcar(mensagemDeErro(erro, 'Não foi possível marcar a referência.'));
      } finally {
        setGravando(false);
      }
    },
    [recarregar],
  );

  const pedirReferencia = useCallback(
    (exame: AudiometryHistoryExam) => {
      setErroAoMarcar(null);
      // Já existindo referência efetiva, trocar muda todas as comparações: passa pelo diálogo.
      if (dados?.effectiveReference) setCandidata(exame);
      else void marcar(exame);
    },
    [dados, marcar],
  );

  const colunas = useMemo<DataTableColumn<AudiometryHistoryExam>[]>(
    () => [
      { id: 'data', label: 'Data', primary: true, render: (e) => formatarDia(e.date) },
      { id: 'papel', label: 'Papel', render: (e) => ROTULO_DO_PAPEL[e.role] },
      {
        id: 'mediaBaixa',
        label: 'Média 500–2.000 Hz (D / E)',
        render: (e) => formatarMedias(e.averages.low),
      },
      {
        id: 'mediaAlta',
        label: 'Média 3.000–6.000 Hz (D / E)',
        render: (e) => formatarMedias(e.averages.high),
      },
      {
        id: 'situacao',
        label: 'Situação',
        render: (e) => {
          const situacao = situacaoDoExame(e);
          return (
            <Box>
              <Chip size="small" label={situacao.rotulo} color={situacao.cor} variant={temLimiares(e) ? 'filled' : 'outlined'} />
              {!temLimiares(e) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Abra o atendimento para registrar
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        id: 'acoes',
        label: 'Ações',
        render: (e) => {
          const dia = formatarDia(e.date);
          return (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                component={RouterLink}
                to={`/medical-consultations/${e.appointmentId}`}
                size="small"
                variant="text"
                aria-label={`Ver exame de ${dia}`}
                sx={{ minHeight: 40 }}
              >
                Ver exame
              </Button>
              {temLimiares(e) && e.role !== 'REFERENCE' && (
                <ActionButton
                  acao={PERMISSAO_DE_MARCAR}
                  rotulo={`Usar como referência a audiometria de ${dia}`}
                  size="small"
                  variant="outlined"
                  sx={{ minHeight: 40 }}
                  onClick={() => pedirReferencia(e)}
                >
                  Usar como referência
                </ActionButton>
              )}
            </Stack>
          );
        },
      },
    ],
    [pedirReferencia],
  );

  if (!historico.permitido) return null;

  const situacaoGeral = ultimo ? situacaoDoExame(ultimo) : null;
  const piora = ultimo?.comparison?.status === 'WORSENED' ? ultimo.comparison : null;
  const algumAgravamento = exames.some((e) => e.comparison?.status === 'WORSENED');
  const semReferenciaEfetiva = dados !== null && !dados.effectiveReference && exames.some(temLimiares);
  const nomeCompleto = employeeName?.trim() || 'este trabalhador';

  const conteudo = (): React.ReactNode => {
    if (!historico.resolvida || historico.carregando) {
      return (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={32} />
          ))}
        </Stack>
      );
    }

    if (historico.erro || !dados) {
      return (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={recarregar}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar a evolução auditiva.
        </Alert>
      );
    }

    if (exames.length === 0) {
      return (
        <EmptyState
          config={{
            title: `Nenhuma audiometria registrada para ${nomeCompleto}`,
            description:
              'A evolução só aparece depois do primeiro exame, e a primeira audiometria vira a referência dele. O exame é registrado na aba Exames Complementares do atendimento.',
            action: {
              label: `Abrir atendimentos ${deQuem}`,
              node: 'medical-consultations',
              deniedHint: 'Peça a um administrador para abrir os atendimentos do trabalhador.',
              onClick: () => navigate('/medical-consultations'),
              allowed: !abrirAtendimentos.disabled,
            },
          }}
        />
      );
    }

    return (
      <Stack spacing={2.5}>
        {erroAoMarcar && !candidata && (
          <Alert severity="error" variant="outlined" onClose={() => setErroAoMarcar(null)}>
            {erroAoMarcar}
          </Alert>
        )}

        {semReferenciaEfetiva && (
          <Alert severity="warning" variant="outlined">
            {dados.reference
              ? `A audiometria que era a referência de ${nomeCompleto} (${formatarDia(dados.reference.date)}) deixou de estar concluída, e sem ela não há com o que comparar. Marque outra audiometria como referência na tabela abaixo.`
              : `${nomeCompleto} ainda não tem uma audiometria de referência. Marque uma na tabela abaixo para voltar a comparar.`}
          </Alert>
        )}

        {ultimo && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              {[
                dados.effectiveReference && dados.reference ? `Referência: ${formatarDia(dados.reference.date)}` : null,
                `Último exame: ${formatarDia(ultimo.date)}`,
                contarAudiometrias(exames.length),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {piora && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Houve piora em relação à referência.</strong> {descreverPiora(piora.flagged)}
              </Typography>
            )}
          </Box>
        )}

        {ultimo && (
          <Box>
            <Grid container spacing={2}>
              {(['right', 'left'] as const).map((ear) => (
                <Grid key={ear} size={{ xs: 12, md: 6 }}>
                  <AudiogramaDaOrelha ear={ear} ultimo={ultimo} referencia={ultimoEhAReferencia ? null : referencia} />
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Legenda texto={`${formatarDia(ultimo.date)} (último exame)`} tracejada={false} />
              {referencia && !ultimoEhAReferencia && (
                <Legenda texto={`${formatarDia(referencia.date)} (referência)`} tracejada />
              )}
              <Typography variant="body2" color="text.secondary">
                Quanto mais para baixo, pior a audição.
              </Typography>
            </Stack>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Histórico de audiometrias
          </Typography>
          <DataTable
            columns={colunas}
            rows={exames}
            getRowId={(e) => String(e.appointmentExamId)}
            emptyState={{
              title: 'Nenhuma audiometria no histórico deste trabalhador',
              description: 'As audiometrias concluídas nos atendimentos aparecem aqui.',
            }}
          />
          {algumAgravamento && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              ▲ = piora de 15 dB ou mais em 3.000, 4.000 ou 6.000 Hz
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <Card variant="outlined" sx={{ borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <Box sx={{ color: ACCENT, display: 'flex' }}>
            <Hearing fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Evolução auditiva
          </Typography>
          {situacaoGeral && !historico.erro && (
            <Chip label={situacaoGeral.rotulo} color={situacaoGeral.cor} size="medium" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        {conteudo()}
      </CardContent>

      <ConfirmDialog
        open={candidata !== null}
        title={`Trocar a referência ${deQuem}?`}
        message={
          candidata && dados?.reference ? (
            <>
              Hoje a referência de {nomeCompleto} é a audiometria de {formatarDia(dados.reference.date)}. Se você usar
              a de {formatarDia(candidata.date)}, as próximas comparações partem dela.
              <br />
              <br />A audiometria de {formatarDia(dados.reference.date)} continua guardada, só deixa de ser o ponto de
              comparação. Você pode voltar atrás a qualquer momento.
            </>
          ) : null
        }
        confirmLabel="Trocar a referência"
        confirmColor="primary"
        cancelLabel="Manter a referência atual"
        loading={gravando}
        error={erroAoMarcar}
        onConfirm={() => candidata && void marcar(candidata)}
        onCancel={() => {
          setCandidata(null);
          setErroAoMarcar(null);
        }}
      />
    </Card>
  );
};

export default EmployeeAudiometryCard;
